import { Injectable } from '@angular/core';
import { createAuth0Client } from "@auth0/auth0-spa-js";
import {Auth0Client} from '@auth0/auth0-spa-js/dist/typings/Auth0Client';
import { BehaviorSubject, Observable, combineLatest, from, of, throwError } from 'rxjs';
import { catchError, concatMap, shareReplay, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ActionsService } from 'src/app/akita/tasks/state/actions.service';
import { environment } from 'src/environments/environment';
@Injectable({
  providedIn: 'root'
})
export class AuthPublicPacingService {
  // Create an observable of Auth0 instance of client
  auth0Client$ = (from(
    createAuth0Client({
      domain: environment.auth_public_facing.domain,
      clientId: environment.auth_public_facing.client_id,
      authorizationParams: {
        redirect_uri: `${window.location.origin}/${environment.auth_public_facing.redirect_uri}`,
        connection: environment.auth_public_facing.connection,
        audience: environment.auth_public_facing.audience,
      }
    })
  ) as Observable<Auth0Client>).pipe(
    shareReplay(1), // Every subscription receives the same shared value
    catchError(err => throwError(err))
  );

  // Define observables for SDK methods that return promises by default
  // For each Auth0 SDK method, first ensure the client instance is ready
  // concatMap: Using the client instance, call SDK method; SDK returns a promise
  // from: Convert that resulting promise into an observable
  isAuthenticated$ = this.auth0Client$.pipe(
    concatMap((client: Auth0Client) => from(client.isAuthenticated())),
    tap(res => this.loggedIn = res)
  );
  handleRedirectCallback$ = this.auth0Client$.pipe(
    concatMap((client: Auth0Client) => from(client.handleRedirectCallback()))
  );
  // Create subject and public observable of user profile data
  private userProfileSubject$ = new BehaviorSubject<any>(null);
  userProfile$ = this.userProfileSubject$.asObservable();
  // Create a local property for login status
  loggedIn: boolean = null;

  constructor(private router: Router,public _action:ActionsService) {
    // On initial load, check authentication state with authorization server
    // Set up local auth streams if user is already authenticated
    this.localAuthSetup();
    // Handle redirect from Auth0 login
    this.handleAuthCallback();
  }

  // When calling, options can be passed if desired
  // https://auth0.github.io/auth0-spa-js/classes/auth0client.html#getuser
  getUser$(options?): Observable<any> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getUser())),
      tap(user => this.userProfileSubject$.next(user))
    );
  }
  getTokenSilently$(options?): Observable<any> {
    return this.auth0Client$.pipe(
    concatMap((client: Auth0Client) => from(client.getTokenSilently(options))));
 }
 getIdTokenClaims$(options?): Observable<any> {
  return this.auth0Client$.pipe(
  concatMap((client: Auth0Client) => from(client.getIdTokenClaims())));
}
  private localAuthSetup() {
    // This should only be called on app initialization
    // Set up local authentication streams
    const checkAuth$ = this.isAuthenticated$.pipe(
      concatMap((loggedIn: boolean) => {
        if (loggedIn) {
          // If authenticated, get user and set in app
          // NOTE: you could pass options here if needed
          return this.getUser$();
        }
        // If not authenticated, return stream that emits 'false'
        return of(loggedIn);
      })
    );
    checkAuth$.subscribe();
  }

  login(redirectPath: string = '/') {
    // A desired redirect path can be passed to login method
    // (e.g., from a route guard)
    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log in
      client.loginWithRedirect({
        authorizationParams: {
          redirect_uri: `${window.location.origin}/${environment.auth_public_facing.redirect_uri}`,
          appState: `${window.location.origin}/${environment.auth_public_facing.redirect_uri}`,
          connection: environment.auth_public_facing.connection,
          audience: environment.auth_public_facing.audience,
        }
      });
    });
  }

  private handleAuthCallback() {
    // Call when app reloads after user logs in with Auth0
    const params = window.location.search;
    if (params.includes('code=') && params.includes('state=')) {
      // let targetRoute: string; // Path to redirect to after login processsed
      const authComplete$ = this.handleRedirectCallback$.pipe(
        // Have client, now call method to handle auth callback redirect
        tap(cbRes => {
          // Get and set target redirect route from callback results
          // targetRoute = cbRes.appState && cbRes.appState.target ? cbRes.appState.target : '/';
        }),
        concatMap(() => {
          // Redirect callback complete; get user and login status
          return combineLatest([
            this.getUser$(),
            this.isAuthenticated$,
            this.getTokenSilently$()
          ]);
        })
      );
      // Subscribe to authentication completion observable
      // Response will be an array of user and login status
      authComplete$.subscribe((res) => {
        // Redirect to target route after callback processing
        this._action.onClickPublicFacing(res);
        // this.router.navigate([targetRoute]);
      });
    }
  }
}
